import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';

// Currency options matching mobile app
const CURRENCIES = [
  { symbol: '$', code: 'USD', name: 'US Dollar' },
  { symbol: '£', code: 'GBP', name: 'British Pound' },
  { symbol: '€', code: 'EUR', name: 'Euro' },
  { symbol: '¥', code: 'JPY', name: 'Japanese Yen' },
  { symbol: 'C$', code: 'CAD', name: 'Canadian Dollar' },
  { symbol: 'A$', code: 'AUD', name: 'Australian Dollar' },
];

const TAX_TYPES = [
  { value: 'vat', label: 'VAT' },
  { value: 'sales_tax', label: 'Sales Tax' },
  { value: 'gst', label: 'GST' },
];

const LABOR_TYPES = [
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
];

// Section navigation items
const SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'fa-chart-line' },
  { id: 'profile', label: 'Business Profile', icon: 'fa-building' },
  { id: 'pricing', label: 'Pricing & Tax', icon: 'fa-tags' },
  { id: 'details', label: 'Business Details', icon: 'fa-id-card' },
  { id: 'banking', label: 'Banking', icon: 'fa-university' },
  { id: 'integrations', label: 'Integrations', icon: 'fa-plug' },
  { id: 'data', label: 'Data & Privacy', icon: 'fa-database' },
  { id: 'subscription', label: 'Subscription', icon: 'fa-crown' },
];

function Account() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeSection, setActiveSection] = useState('dashboard');
  const sectionRefs = useRef({});

  // Form state
  const [formData, setFormData] = useState({
    business_name: '',
    phone: '',
    address_street: '',
    address_street2: '',
    address_city: '',
    address_state: '',
    address_zip: '',
    preferred_currency: '$',
    default_hourly_rate: '',
    default_labor_type: 'hourly',
    tax_enabled: false,
    tax_type: 'sales_tax',
    default_tax_rate: '',
    business_email: '',
    business_website: '',
    tax_id_number: '',
    show_tax_id_on_pdf: false,
    business_registration_number: '',
    show_business_registration_on_pdf: false,
    bank_name: '',
    bank_account_holder: '',
    bank_account_number: '',
    bank_routing_number: '',
    bank_iban: '',
    bank_swift_bic: '',
    payment_instructions: '',
    show_bank_details_on_pdf: false,
  });

  useEffect(() => {
    // Check for Stripe success/canceled params
    if (searchParams.get('success')) {
      setSuccessMessage('Payment successful! Your Pro membership is now active.');
      setSearchParams({});
    } else if (searchParams.get('canceled')) {
      setError('Payment was canceled. You can try again anytime.');
      setTimeout(() => setError(''), 5000);
      setSearchParams({});
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [profileData, statsData, calendarData] = await Promise.all([
        api.getProfile(),
        api.getUserStats(),
        api.getGoogleCalendarStatus().catch(() => ({ connected: false })),
      ]);

      setUser(profileData);
      setStats(statsData);
      setCalendarStatus(calendarData);

      // Populate form with existing data
      setFormData({
        business_name: profileData.business_name || '',
        phone: profileData.phone || '',
        address_street: profileData.address_street || '',
        address_street2: profileData.address_street2 || '',
        address_city: profileData.address_city || '',
        address_state: profileData.address_state || '',
        address_zip: profileData.address_zip || '',
        preferred_currency: profileData.preferred_currency || '$',
        default_hourly_rate: profileData.default_hourly_rate || '',
        default_labor_type: profileData.default_labor_type || 'hourly',
        tax_enabled: profileData.tax_enabled || false,
        tax_type: profileData.tax_type || 'sales_tax',
        default_tax_rate: profileData.default_tax_rate || '',
        business_email: profileData.business_email || '',
        business_website: profileData.business_website || '',
        tax_id_number: profileData.tax_id_number || '',
        show_tax_id_on_pdf: profileData.show_tax_id_on_pdf || false,
        business_registration_number: profileData.business_registration_number || '',
        show_business_registration_on_pdf: profileData.show_business_registration_on_pdf || false,
        bank_name: profileData.bank_name || '',
        bank_account_holder: profileData.bank_account_holder || '',
        bank_account_number: profileData.bank_account_number || '',
        bank_routing_number: profileData.bank_routing_number || '',
        bank_iban: profileData.bank_iban || '',
        bank_swift_bic: profileData.bank_swift_bic || '',
        payment_instructions: profileData.payment_instructions || '',
        show_bank_details_on_pdf: profileData.show_bank_details_on_pdf || false,
      });
    } catch (err) {
      if (err.message.includes('401')) {
        navigate('/login');
        return;
      }
      setError('Failed to load account data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.updateProfile(formData);
      setSuccessMessage('Profile saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to save profile: ' + err.message);
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/create-checkout-session/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = await response.json();
      if (response.ok && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setError('Failed to create checkout session');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/stripe/create-portal-session/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = await response.json();
      if (response.ok && data.portal_url) {
        window.location.href = data.portal_url;
      } else {
        setError('Failed to open subscription management');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    try {
      const data = await api.getGoogleCalendarAuthUrl();
      if (data.auth_url) {
        window.open(data.auth_url, '_blank');
        // Poll for connection status
        const pollInterval = setInterval(async () => {
          const status = await api.getGoogleCalendarStatus();
          if (status.connected) {
            setCalendarStatus(status);
            clearInterval(pollInterval);
          }
        }, 3000);
        setTimeout(() => clearInterval(pollInterval), 60000);
      }
    } catch (err) {
      setError('Failed to connect calendar');
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar? Your jobs will no longer sync.')) return;
    try {
      await api.disconnectGoogleCalendar();
      setCalendarStatus({ connected: false });
      setSuccessMessage('Google Calendar disconnected');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to disconnect calendar');
    }
  };

  const handleExportData = async () => {
    try {
      const csvData = await api.exportData();
      const blob = new Blob([csvData], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scope_order_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setSuccessMessage('Data exported successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Failed to export data');
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) return;
    if (!window.confirm('This will permanently delete ALL your data. Are you absolutely sure?')) return;

    try {
      await api.deleteAccount();
      localStorage.clear();
      navigate('/');
    } catch (err) {
      setError('Failed to delete account');
    }
  };

  const handleLogout = async () => {
    await api.logout();
    navigate('/login');
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const formatCurrency = (amount) => {
    const symbol = formData.preferred_currency || '$';
    return `${symbol}${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="py-20 px-6 bg-slate-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-primary mb-4"></i>
          <p className="text-slate-600">Loading your account...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Toast Messages */}
      {successMessage && (
        <div className="fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in">
          <i className="fas fa-check-circle"></i>
          {successMessage}
        </div>
      )}
      {error && (
        <div className="fixed top-4 right-4 z-50 bg-red-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="hidden lg:block w-64 bg-primary min-h-screen sticky top-0">
          <div className="p-6">
            <h2 className="text-white font-black text-xl mb-1">Account</h2>
            <p className="text-white/60 text-sm">{user?.email}</p>
          </div>
          <nav className="px-3">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`w-full text-left px-4 py-3 rounded-lg mb-1 flex items-center gap-3 transition-all ${
                  activeSection === section.id
                    ? 'bg-white/20 text-white'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <i className={`fas ${section.icon} w-5`}></i>
                <span className="font-medium">{section.label}</span>
              </button>
            ))}
          </nav>
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-3 rounded-lg text-white/70 hover:bg-white/10 hover:text-white transition flex items-center gap-3"
            >
              <i className="fas fa-sign-out-alt w-5"></i>
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-10 max-w-5xl">
          {/* Mobile Header */}
          <div className="lg:hidden mb-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-black text-primary">Account</h1>
              <p className="text-slate-500 text-sm">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-slate-500 hover:text-primary"
            >
              <i className="fas fa-sign-out-alt text-xl"></i>
            </button>
          </div>

          {/* Dashboard Section */}
          <section ref={el => sectionRefs.current['dashboard'] = el} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <i className="fas fa-chart-line text-white"></i>
              </div>
              <h2 className="text-xl font-black text-primary">Dashboard</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <i className="fas fa-paper-plane text-orange-500"></i>
                  <span className="text-slate-500 text-sm font-medium uppercase tracking-wide">Orders Sent</span>
                </div>
                <p className="text-3xl font-black text-primary">{stats?.orders_sent || 0}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <i className="fas fa-wallet text-green-500"></i>
                  <span className="text-slate-500 text-sm font-medium uppercase tracking-wide">Total Earned</span>
                </div>
                <p className="text-3xl font-black text-primary">{formatCurrency(stats?.total_earned)}</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <i className="fas fa-calendar-alt text-blue-500"></i>
                  <span className="text-slate-500 text-sm font-medium uppercase tracking-wide">This Month</span>
                </div>
                <p className="text-3xl font-black text-primary">{formatCurrency(stats?.this_month)}</p>
              </div>
            </div>
          </section>

          {/* Business Profile Section */}
          <section ref={el => sectionRefs.current['profile'] = el} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <i className="fas fa-building text-white"></i>
              </div>
              <h2 className="text-xl font-black text-primary">Business Profile</h2>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Business Name</label>
                  <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    placeholder="Your Business Name"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 000-0000"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 mt-4">
                <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3">Business Address</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    name="address_street"
                    value={formData.address_street}
                    onChange={handleInputChange}
                    placeholder="Address Line 1"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  />
                  <input
                    type="text"
                    name="address_street2"
                    value={formData.address_street2}
                    onChange={handleInputChange}
                    placeholder="Address Line 2 (optional)"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <input
                      type="text"
                      name="address_city"
                      value={formData.address_city}
                      onChange={handleInputChange}
                      placeholder="City"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                    />
                    <input
                      type="text"
                      name="address_state"
                      value={formData.address_state}
                      onChange={handleInputChange}
                      placeholder="State/County"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                    />
                    <input
                      type="text"
                      name="address_zip"
                      value={formData.address_zip}
                      onChange={handleInputChange}
                      placeholder="Postcode"
                      className="col-span-2 md:col-span-2 w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                    />
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* Pricing & Tax Section */}
          <section ref={el => sectionRefs.current['pricing'] = el} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <i className="fas fa-tags text-white"></i>
              </div>
              <h2 className="text-xl font-black text-primary">Pricing & Tax</h2>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Currency</label>
                  <select
                    name="preferred_currency"
                    value={formData.preferred_currency}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  >
                    {CURRENCIES.map(c => (
                      <option key={c.code} value={c.symbol}>{c.symbol} - {c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Default Hourly Rate</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">{formData.preferred_currency}</span>
                    <input
                      type="number"
                      name="default_hourly_rate"
                      value={formData.default_hourly_rate}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      step="0.01"
                      className="w-full pl-10 pr-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Labor Type</label>
                  <select
                    name="default_labor_type"
                    value={formData.default_labor_type}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  >
                    {LABOR_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-slate-700">Tax Configuration</h3>
                    <p className="text-sm text-slate-500">Apply tax to your quotes and invoices</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      name="tax_enabled"
                      checked={formData.tax_enabled}
                      onChange={handleInputChange}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-orange-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                </div>

                {formData.tax_enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pl-4 border-l-4 border-orange-500">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Tax Type</label>
                      <select
                        name="tax_type"
                        value={formData.tax_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                      >
                        {TAX_TYPES.map(t => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Default Tax Rate (%)</label>
                      <input
                        type="number"
                        name="default_tax_rate"
                        value={formData.default_tax_rate}
                        onChange={handleInputChange}
                        placeholder="0"
                        step="0.1"
                        min="0"
                        max="100"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                      />
                    </div>
                  </div>
                )}
              </div>

            </div>
          </section>

          {/* Business Details Section */}
          <section ref={el => sectionRefs.current['details'] = el} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <i className="fas fa-id-card text-white"></i>
              </div>
              <h2 className="text-xl font-black text-primary">Business Details</h2>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <p className="text-sm text-slate-500 mb-4">These details appear on your quotes and invoices</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Business Email</label>
                  <input
                    type="email"
                    name="business_email"
                    value={formData.business_email}
                    onChange={handleInputChange}
                    placeholder="contact@yourbusiness.com"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">Business Website</label>
                  <input
                    type="url"
                    name="business_website"
                    value={formData.business_website}
                    onChange={handleInputChange}
                    placeholder="https://yourbusiness.com"
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1 mr-4">
                    <label className="block text-sm font-bold text-slate-700 mb-2">Tax/VAT ID Number</label>
                    <input
                      type="text"
                      name="tax_id_number"
                      value={formData.tax_id_number}
                      onChange={handleInputChange}
                      placeholder="Enter your tax ID"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition bg-white"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                    <input
                      type="checkbox"
                      name="show_tax_id_on_pdf"
                      checked={formData.show_tax_id_on_pdf}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-600">Show on PDF</span>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1 mr-4">
                    <label className="block text-sm font-bold text-slate-700 mb-2">Business Registration Number</label>
                    <input
                      type="text"
                      name="business_registration_number"
                      value={formData.business_registration_number}
                      onChange={handleInputChange}
                      placeholder="Enter registration number"
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition bg-white"
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                    <input
                      type="checkbox"
                      name="show_business_registration_on_pdf"
                      checked={formData.show_business_registration_on_pdf}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                    />
                    <span className="text-sm text-slate-600">Show on PDF</span>
                  </label>
                </div>
              </div>

            </div>
          </section>

          {/* Banking Section */}
          <section ref={el => sectionRefs.current['banking'] = el} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <i className="fas fa-university text-white"></i>
              </div>
              <h2 className="text-xl font-black text-primary">Banking & Payment</h2>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                <i className="fas fa-exclamation-triangle text-amber-600 mt-0.5"></i>
                <p className="text-sm text-amber-800">
                  Banking details will be visible to anyone who receives your PDF quotes and invoices.
                </p>
              </div>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-bold text-slate-700">Show Banking Details on PDFs</h3>
                  <p className="text-sm text-slate-500">Include payment information on quotes and invoices</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="show_bank_details_on_pdf"
                    checked={formData.show_bank_details_on_pdf}
                    onChange={handleInputChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 rounded-full peer peer-checked:bg-orange-500 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
              </div>

              {formData.show_bank_details_on_pdf && (
                <div className="space-y-4 pl-4 border-l-4 border-orange-500">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Bank Name</label>
                      <input
                        type="text"
                        name="bank_name"
                        value={formData.bank_name}
                        onChange={handleInputChange}
                        placeholder="Bank Name"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Account Holder Name</label>
                      <input
                        type="text"
                        name="bank_account_holder"
                        value={formData.bank_account_holder}
                        onChange={handleInputChange}
                        placeholder="Account Holder"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Account Number</label>
                      <input
                        type="text"
                        name="bank_account_number"
                        value={formData.bank_account_number}
                        onChange={handleInputChange}
                        placeholder="Account Number"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Routing/Sort Code</label>
                      <input
                        type="text"
                        name="bank_routing_number"
                        value={formData.bank_routing_number}
                        onChange={handleInputChange}
                        placeholder="Routing Number"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">IBAN</label>
                      <input
                        type="text"
                        name="bank_iban"
                        value={formData.bank_iban}
                        onChange={handleInputChange}
                        placeholder="International Bank Account Number"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">SWIFT/BIC Code</label>
                      <input
                        type="text"
                        name="bank_swift_bic"
                        value={formData.bank_swift_bic}
                        onChange={handleInputChange}
                        placeholder="SWIFT/BIC Code"
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2">Payment Instructions</label>
                    <textarea
                      name="payment_instructions"
                      value={formData.payment_instructions}
                      onChange={handleInputChange}
                      placeholder="Additional payment instructions or notes..."
                      rows={3}
                      className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none transition resize-none"
                    />
                  </div>
                </div>
              )}

            </div>
          </section>

          {/* Integrations Section */}
          <section ref={el => sectionRefs.current['integrations'] = el} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <i className="fas fa-plug text-white"></i>
              </div>
              <h2 className="text-xl font-black text-primary">Integrations</h2>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center">
                    <i className="fab fa-google text-2xl text-slate-600"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">Google Calendar</h3>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${calendarStatus?.connected ? 'bg-green-500' : 'bg-slate-300'}`}></span>
                      <span className="text-sm text-slate-500">
                        {calendarStatus?.connected ? 'Connected' : 'Not connected'}
                      </span>
                    </div>
                  </div>
                </div>
                {calendarStatus?.connected ? (
                  <button
                    onClick={handleDisconnectCalendar}
                    className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    onClick={handleConnectCalendar}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition"
                  >
                    Connect
                  </button>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-4">
                Sync your scheduled jobs with Google Calendar to keep track of appointments.
              </p>
            </div>
          </section>

          {/* Data & Privacy Section */}
          <section ref={el => sectionRefs.current['data'] = el} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <i className="fas fa-database text-white"></i>
              </div>
              <h2 className="text-xl font-black text-primary">Data & Privacy</h2>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-slate-900">Export Your Data</h3>
                  <p className="text-sm text-slate-500">Download all your data as a CSV file</p>
                </div>
                <button
                  onClick={handleExportData}
                  className="px-4 py-2 text-sm font-medium text-primary border-2 border-primary hover:bg-primary hover:text-white rounded-lg transition flex items-center gap-2"
                >
                  <i className="fas fa-download"></i>
                  Export CSV
                </button>
              </div>

              <div className="border-t border-slate-100 pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-red-600">Delete Account</h3>
                    <p className="text-sm text-slate-500">Permanently delete your account and all data</p>
                  </div>
                  <button
                    onClick={handleDeleteAccount}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition"
                  >
                    Delete Account
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Subscription Section */}
          <section ref={el => sectionRefs.current['subscription'] = el} className="mb-24">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <i className="fas fa-crown text-white"></i>
              </div>
              <h2 className="text-xl font-black text-primary">Subscription</h2>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
              {user?.is_premium ? (
                <div>
                  <div className="flex items-center justify-between mb-6 p-4 bg-green-50 rounded-xl border border-green-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                        <i className="fas fa-crown text-white"></i>
                      </div>
                      <div>
                        <div className="font-black text-green-700">PRO MEMBERSHIP</div>
                        <div className="text-sm text-slate-600">$19/month - Active</div>
                      </div>
                    </div>
                    <i className="fas fa-check-circle text-green-500 text-xl"></i>
                  </div>
                  <button
                    onClick={handleManageSubscription}
                    className="bg-slate-100 text-slate-700 px-6 py-3 rounded-xl font-bold hover:bg-slate-200 transition"
                  >
                    Manage Subscription
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center gap-3 mb-6 p-4 bg-slate-50 rounded-xl">
                    <div className="w-10 h-10 bg-slate-400 rounded-full flex items-center justify-center">
                      <i className="fas fa-user text-white"></i>
                    </div>
                    <div>
                      <div className="font-black text-slate-700">FREE ACCOUNT</div>
                      <div className="text-sm text-slate-500">Limited features</div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-slate-600">
                      <i className="fas fa-check text-green-500 mr-3 w-5"></i>
                      <span>Unlimited change orders</span>
                    </div>
                    <div className="flex items-center text-slate-600">
                      <i className="fas fa-check text-green-500 mr-3 w-5"></i>
                      <span>Unlimited clients & jobs</span>
                    </div>
                    <div className="flex items-center text-slate-600">
                      <i className="fas fa-check text-green-500 mr-3 w-5"></i>
                      <span>Professional PDF quotes & invoices</span>
                    </div>
                    <div className="flex items-center text-slate-600">
                      <i className="fas fa-check text-green-500 mr-3 w-5"></i>
                      <span>Google Calendar sync</span>
                    </div>
                  </div>

                  <button
                    onClick={handleUpgrade}
                    className="w-full bg-orange-500 text-white px-6 py-4 rounded-xl font-bold hover:bg-orange-600 transition text-lg shadow-lg"
                  >
                    Upgrade to Pro - $19/month
                  </button>
                </div>
              )}
            </div>
          </section>

        </main>
      </div>

      {/* Floating Save Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-40">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between lg:ml-64">
          <p className="text-sm text-slate-500 hidden sm:block">
            <i className="fas fa-info-circle mr-2"></i>
            Changes are saved when you click the button
          </p>
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="bg-orange-500 text-white px-8 py-3 rounded-xl font-bold hover:bg-orange-600 transition disabled:opacity-50 flex items-center gap-2 shadow-lg ml-auto"
          >
            {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

export default Account;
